import { Box } from "@rebass/grid";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as contractHooks from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import {
  isRequestingRentioMoveService,
  requestRentioMoveServiceError,
} from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.selectors";
import { EContractFetchType } from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.types";
import * as contractUtils from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.utils";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  ESpacings,
  Loading,
  Modal,
  MultiStepForm,
} from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import React from "react";
import { requestRentioMoveServiceSchema } from "../../forms/requestRentioMoveService";
import {
  performingSubmitActionResultSelector,
  submitActionCreator,
} from "../../forms/requestRentioMoveService/schema.requestRentioMoveService.actions";
import { ts } from "../../services";

const { i18n } = utils;

enum EStep {
  Intro,
  Form,
  Outro,
}

interface IProps {
  property: IProperty;
  contract: IContract;
  onClose: () => void;
  energyTransferByMexp: boolean;
}

export const RequestRentioMoveServiceModal = (props: IProps) => {
  const { property, contract, energyTransferByMexp, onClose } = props;

  const [formId] = React.useState(generateFormId());
  const [step, setStep] = React.useState<EStep>(EStep.Intro);

  const activeContractsForPropertyFilter = contractUtils.getFilterForProperty(
    property.id,
    EContractFetchType.Active,
  );

  const endedContractsForPropertyFilter = contractUtils.getFilterForProperty(
    property.id,
    EContractFetchType.Ended,
  );

  const { items: activeContracts = [], isFetching: isFetchingActiveContracts } =
    contractHooks.useGetAll({
      shouldRefetch: true,
      query: {
        filter: activeContractsForPropertyFilter,
        sort: [{ field: "startDate", method: "DESC" }],
      },
    });

  const { items: endedContracts = [], isFetching: isFetchingEndedContracts } =
    contractHooks.useGetAll({
      shouldRefetch: true,
      query: {
        filter: endedContractsForPropertyFilter,
        sort: [{ field: "startDate", method: "DESC" }],
      },
    });

  const getPreviousContract = () => {
    if (activeContracts?.length > 1) {
      const otherActiveContracts = activeContracts.filter(
        otherContract => otherContract?.id !== contract?.id,
      );

      const previousActive = otherActiveContracts
        .filter(c => new Date(c.startDate) < new Date(contract.startDate))
        .sort(
          (a, b) =>
            new Date(contract.startDate).getTime() -
            new Date(a.startDate).getTime() -
            (new Date(contract.startDate).getTime() -
              new Date(b.startDate).getTime()),
        );

      return previousActive[0];
    }

    if (endedContracts?.length > 0) {
      const previousEnded = endedContracts
        .filter(
          endedContract =>
            new Date(endedContract.stopDate || "") <
            new Date(contract.startDate),
        )
        .sort(
          (a, b) =>
            new Date(contract.startDate).getTime() -
            new Date(a.stopDate || "").getTime() -
            (new Date(contract.startDate).getTime() -
              new Date(b.stopDate || "").getTime()),
        );

      return previousEnded[0];
    }

    return undefined;
  };

  const isLoading = isFetchingActiveContracts || isFetchingEndedContracts;

  const renderIntro = () => (
    <>
      <Modal
        width="medium"
        onClose={onClose}
        heading={getLocalizedText("request_rentio_move_service.modal.heading")}
        actions={[
          {
            content: ts.system("cancel"),
            onClick: onClose,
            appearance: "outline",
          },
          {
            content: ts.system("next"),
            onClick: () => setStep(EStep.Form),
            appearance: "primary",
          },
        ]}
      >
        {isLoading ? (
          <Box mb={ESpacings.base}>
            <Loading />
          </Box>
        ) : (
          i18n.tm("request_rentio_move_service.modal.intro.content", {
            markdownProps: { listAsChecklist: true },
          })
        )}
      </Modal>
    </>
  );

  const renderForm = () => {
    return (
      <MultiStepForm
        formId={`request-move_service-services-${formId}`}
        schemas={requestRentioMoveServiceSchema({
          property,
          contract,
          previousContract: getPreviousContract(),
        })}
        asModal={true}
        withAside={false}
        onPressPreviousFirstStep={() => {
          setStep(EStep.Intro);
        }}
        modalProps={{
          onClose,
          heading: getLocalizedText(
            "request_rentio_move_service.modal.heading",
          ),
          width: "medium",
        }}
        performingSubmitActionResultSelector={performingSubmitActionResultSelector(
          contract.id,
        )}
        isPerformingSubmitActionSelector={state =>
          isRequestingRentioMoveService(state, contract.id)
        }
        performingSubmitActionErrorSelector={state =>
          requestRentioMoveServiceError(state, contract.id)
        }
        submitActionCreator={submitActionCreator({
          property,
          contract,
          energyTransferByMexp,
        })}
        onSuccess={() => setStep(EStep.Outro)}
        submitLabel={getLocalizedText("system.submit")}
      />
    );
  };

  const renderOutro = () => {
    return (
      <Modal
        width="medium"
        onClose={onClose}
        heading={getLocalizedText("request_rentio_move_service.modal.heading")}
        actions={[
          {
            content: getLocalizedText("system.check"),
            onClick: onClose,
            appearance: "primary",
          },
        ]}
      >
        {i18n.tm("request_rentio_move_service.modal.outro.content", {
          markdownProps: { listAsChecklist: true },
        })}
      </Modal>
    );
  };

  switch (step) {
    case EStep.Form:
      return renderForm();

    case EStep.Outro:
      return renderOutro();

    case EStep.Intro:
    default:
      return renderIntro();
  }
};
